import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

export default function RawMaterialDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [material, setMaterial] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState({
    purchaseDate: new Date().toISOString().split("T")[0],
    quantityPurchased: "",
    totalCost: "",
    supplierName: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);

  useEffect(() => {
    fetchMaterialDetails();
    fetchSuppliers();
  }, [id]);

  const fetchMaterialDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/inventory/raw-materials/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setMaterial(data.material);
        setPurchaseHistory(data.material.purchaseHistory || []);
      } else {
        toast.error("Failed to load material details");
        navigate("/inventory?tab=2");
      }
    } catch (error) {
      console.error("Error fetching material details:", error);
      toast.error("Failed to load material details");
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/suppliers/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.suppliers || []);
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const handleAddPurchase = async () => {
    if (!purchaseForm.quantityPurchased || !purchaseForm.totalCost || !purchaseForm.purchaseDate || !purchaseForm.supplierName) {
      toast.error("Please fill in all required fields including supplier name");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      if (editingPurchase) {
        // Update existing purchase
        const response = await fetch(`${API_BASE_URL}/api/inventory/purchases/raw-materials/${editingPurchase.PurchaseId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...purchaseForm,
            quantityPurchased: parseFloat(purchaseForm.quantityPurchased),
            totalCost: parseFloat(purchaseForm.totalCost),
          }),
        });

        if (response.ok) {
          toast.success("Purchase record updated successfully");
        } else {
          toast.error("Failed to update purchase record");
          return;
        }
      } else {
        // Add new purchase
        const response = await fetch(`${API_BASE_URL}/api/inventory/purchases/raw-materials`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            materialId: parseInt(id),
            ...purchaseForm,
            quantityPurchased: parseFloat(purchaseForm.quantityPurchased),
            totalCost: parseFloat(purchaseForm.totalCost),
          }),
        });

        if (response.ok) {
          toast.success("Purchase record added successfully");
        } else {
          toast.error("Failed to add purchase record");
          return;
        }
      }

      setOpenDialog(false);
      setEditingPurchase(null);
      setPurchaseForm({
        purchaseDate: new Date().toISOString().split("T")[0],
        quantityPurchased: "",
        totalCost: "",
        supplierName: "",
      });
      fetchMaterialDetails();
    } catch (error) {
      console.error("Error saving purchase:", error);
      toast.error("Failed to save purchase record");
    }
  };

  const handleEditPurchase = (purchase) => {
    setEditingPurchase(purchase);
    setPurchaseForm({
      purchaseDate: purchase.PurchaseDate.split("T")[0],
      quantityPurchased: purchase.QuantityPurchased,
      totalCost: purchase.TotalCost,
      supplierName: purchase.SupplierName || "",
    });
    setOpenDialog(true);
  };

  const handleDeleteClick = (purchase) => {
    setPurchaseToDelete(purchase);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!purchaseToDelete) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/inventory/purchases/raw-materials/${purchaseToDelete.PurchaseId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Purchase record deleted successfully");
        fetchMaterialDetails();
      } else {
        toast.error("Failed to delete purchase record");
      }
    } catch (error) {
      console.error("Error deleting purchase:", error);
      toast.error("Failed to delete purchase record");
    } finally {
      setDeleteDialogOpen(false);
      setPurchaseToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPurchaseToDelete(null);
  };

  const handleRecalculateStock = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/inventory/raw-materials/${id}/recalculate-stock`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Stock recalculated successfully. Correct stock: ${data.correctStock} ${material.Unit}`);
        fetchMaterialDetails(); // Refresh the page data
      } else {
        toast.error("Failed to recalculate stock");
      }
    } catch (error) {
      console.error("Error recalculating stock:", error);
      toast.error("Failed to recalculate stock");
    }
  };

  if (!material) {
    return (
      <AppLayout>
        <Box sx={{ 
          p: 4, 
          textAlign: "center",
          bgcolor: "#0a0a0a",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Typography sx={{ color: "#C59A58", fontSize: "1.2rem" }}>
            Loading...
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ 
        maxWidth: "1400px", 
        mx: "auto", 
        p: { xs: 1, sm: 2, md: 3 },
        bgcolor: "#0a0a0a",
        minHeight: "100vh"
      }}>
        {/* HEADER */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            letterSpacing: { xs: 1, sm: 2 },
            color: "#C59A58",
            fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.5rem" },
            wordBreak: "break-word",
            flex: 1,
            minWidth: 0
          }}>
            {material.MaterialName.toUpperCase()}
          </Typography>
          <Chip 
            label={`RM-${material.MaterialId}`} 
            sx={{ 
              bgcolor: "#C59A58", 
              color: "#000", 
              fontWeight: 600,
              fontSize: { xs: "0.7rem", sm: "0.85rem", md: "1rem" },
              px: { xs: 1, sm: 1.5, md: 2 },
              py: { xs: 1.5, sm: 2, md: 2.5 },
              height: { xs: 28, sm: 36, md: 40 },
              minWidth: "fit-content"
            }} 
          />
        </Box>

        {/* BACK BUTTON */}
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/inventory?tab=2")}
          sx={{ 
            mb: 2,
            borderColor: "#C59A58",
            color: "#C59A58",
            "&:hover": { borderColor: "#b68649", bgcolor: "rgba(197, 154, 88, 0.04)" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 0.75, sm: 1 }
          }}
        >
          Back to Inventory
        </Button>

        {/* MATERIAL INFO CARD */}
        <Paper elevation={6} sx={{ 
          p: { xs: 1.5, sm: 2, md: 3 }, 
          mb: 3, 
          borderRadius: 2, 
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          width: "100%"
        }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: "#C59A58",
              fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" }
            }}>
              Material Information
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Material Name
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                {material.MaterialName}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Current Stock
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                {material.QuantityInStock}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Unit
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                {material.Unit}
              </Typography>
            </Grid>

            {material.Description && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                  Description
                </Typography>
                <Typography sx={{ color: "#fff", fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" } }}>
                  {material.Description}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* PURCHASE HISTORY HEADER */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 2, flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700,
            color: "#C59A58",
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" }
          }}>
            Purchase History
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingPurchase(null);
              setPurchaseForm({
                purchaseDate: new Date().toISOString().split("T")[0],
                quantityPurchased: "",
                totalCost: "",
                supplierName: "",
              });
              setOpenDialog(true);
            }}
            sx={{ 
              bgcolor: "#C59A58", 
              "&:hover": { bgcolor: "#b68649" },
              fontWeight: 600,
              color: "#000",
              fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 0.6, sm: 0.8 },
              width: { xs: "100%", sm: "auto" }
            }}
          >
            Add Purchase
          </Button>
        </Box>

        {/* PURCHASE HISTORY TABLE */}
        <Paper elevation={6} sx={{ 
          p: { xs: 0.75, sm: 1, md: 2 }, 
          borderRadius: 2, 
          bgcolor: "#1a1a1a",
          border: "1px solid #333",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          width: "100%",
          overflowX: "auto"
        }}>
          <Box sx={{ overflowX: "auto", width: "100%" }}>
            <TableContainer sx={{ 
              borderRadius: 2,
              minWidth: { xs: "100%", sm: 600, md: 700 }
            }}>
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0 8px",
                }}
                aria-label="purchase history table"
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: "#C59A58" }}>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Supplier
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Quantity
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Total Cost
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseHistory.length > 0 ? (
                    purchaseHistory.map((purchase, index) => (
                      <TableRow
                        key={purchase.PurchaseId}
                        sx={{
                          bgcolor: index % 2 === 0 ? "#2a2a2a" : "#333",
                          borderRadius: 1,
                          "&:hover": { boxShadow: 1 },
                          "& td": { 
                            border: "none", 
                            verticalAlign: "middle",
                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.875rem" },
                            p: { xs: 0.6, sm: 0.8, md: 1.5 },
                            whiteSpace: "nowrap"
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: "#C59A58" }}>
                          {new Date(purchase.PurchaseDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#fff" }}>
                          {purchase.ActualSupplierName || purchase.SupplierName || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {purchase.QuantityPurchased} {material.Unit}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#C59A58" }}>
                          ₹{parseFloat(purchase.TotalCost).toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: { xs: 0.25, sm: 0.5, md: 1 }, justifyContent: "center" }}>
                            <IconButton
                              onClick={() => handleEditPurchase(purchase)}
                              sx={{ 
                                color: "#C59A58",
                                p: { xs: 0.3, sm: 0.6, md: 1 }
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteClick(purchase)}
                              sx={{ 
                                color: "#f44336",
                                p: { xs: 0.3, sm: 0.6, md: 1 }
                              }}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 2, border: "none" }}>
                        <Typography variant="body1" sx={{ 
                          color: "#888",
                          fontSize: { xs: "0.7rem", sm: "0.8rem", md: "1rem" }
                        }}>
                          No purchase history yet. Add your first purchase record!
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      </Box>

      {/* ADD/EDIT PURCHASE DIALOG */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2,
            mx: { xs: 1, sm: 0 }
          }
        }}
      >
        <DialogTitle sx={{ color: "#C59A58", fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" }, p: { xs: 1.5, sm: 2 } }}>
          {editingPurchase ? "Edit Purchase Record" : "Add Purchase Record"}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12}>
                <TextField
                  label="Purchase Date"
                  type="date"
                  value={purchaseForm.purchaseDate}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, purchaseDate: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={`Quantity (${material.Unit})`}
                  type="number"
                  value={purchaseForm.quantityPurchased}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, quantityPurchased: e.target.value })}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Total Cost (₹)"
                  type="number"
                  value={purchaseForm.totalCost}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, totalCost: e.target.value })}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel sx={{ color: '#888', '&.Mui-focused': { color: '#C59A58' } }}>
                    Supplier Name *
                  </InputLabel>
                  <Select
                    value={purchaseForm.supplierName}
                    onChange={(e) => setPurchaseForm({ ...purchaseForm, supplierName: e.target.value })}
                    label="Supplier Name *"
                    required
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C59A58' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff',
                      '& .MuiSvgIcon-root': { color: '#C59A58' }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: '#2a2a2a',
                          border: '1px solid #444',
                          '& .MuiMenuItem-root': {
                            color: '#fff',
                            '&:hover': { bgcolor: '#444' },
                            '&.Mui-selected': { bgcolor: '#C59A58', color: '#000' }
                          }
                        }
                      }
                    }}
                  >
                    {suppliers.map((supplier) => (
                      <MenuItem key={supplier.SupplierId} value={supplier.SupplierCode}>
                        {supplier.SupplierCode}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{ color: '#888' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddPurchase} 
            variant="contained" 
            sx={{ 
              bgcolor: "#C59A58", 
              "&:hover": { bgcolor: "#b68649" },
              color: "#000",
              fontWeight: 600
            }}
          >
            {editingPurchase ? "Update" : "Add"} Purchase
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2
          }
        }}
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: "#C59A58" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" sx={{ color: "#fff" }}>
            Are you sure you want to delete this purchase record from {purchaseToDelete && new Date(purchaseToDelete.PurchaseDate).toLocaleDateString()}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel} sx={{ color: "#888" }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
